import { CSSProperties } from 'react';
import { Cell, flexRender } from '@tanstack/react-table';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { OrderItem } from '@/types/order-list';
import { deepEqual } from '@/utils/deep-equal';
import { TABLE_CHECKBOX_CELL_Z_INDEX, TABLE_CHECKBOX_COLUMN_ID } from './tableConstants';

interface DragAlongCellProps<TData extends object> {
  cell: Cell<TData, unknown>; // TanStack Table Cell 타입
  disableDrag?: boolean;
  checkedOrders?: OrderItem[];
  order?: OrderItem; // 현재 셀에 해당하는 주문 정보
}

export const DragAlongCell = <TData extends object>({
  cell,
  order,
  disableDrag = false,
  checkedOrders,
}: DragAlongCellProps<TData>) => {
  const { setNodeRef, transform, isDragging } = useSortable({
    id: cell.column.id,
    disabled: disableDrag,
  });

  const isCheckbox = cell.column.id === TABLE_CHECKBOX_COLUMN_ID;
  const isChecked = order ? checkedOrders?.some((o) => deepEqual(o, order)) ?? false : false;

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: isCheckbox ? 'sticky' : 'relative',
    left: isCheckbox ? 0 : 'auto',
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s',
    maxHeight: '60px',
    // table-fixed 에서 컬럼 폭을 강제하고, 내용이 넘치면 잘라낸다(가로 밀림 방지).
    width: cell.column.getSize(),
    overflow: 'hidden',
    zIndex: isCheckbox ? TABLE_CHECKBOX_CELL_Z_INDEX : isDragging ? 1 : 0,
    background: isChecked
      ? '#F2F0FF' // 체크된 경우 파란색 배경
      : isCheckbox
      ? 'inherit' // ⭐ 반드시 inherit로!
      : isDragging
      ? '#F2F0FF' // 드래그 중 배경
      : 'transparent',
  };

  return (
    <td ref={setNodeRef} style={style} className="h-[60px] max-h-[60px] Body5M14">
      {/* 컬럼이 좁아지면 줄바꿈. 셀 내부의 nowrap/max-w 를 일괄 무력화해 컬럼 폭을 따르게 한다.
          체크박스 셀은 가운데 정렬만 유지. */}
      <div
        className={
          isCheckbox
            ? 'w-full min-w-0'
            : 'w-full min-w-0 whitespace-normal break-words [&_*]:!max-w-none [&_*]:!whitespace-normal'
        }
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </div>
    </td>
  );
};
